import { useEffect, useRef } from "react";
import { UseFormReturn, useWatch } from "react-hook-form";
import { type SLNG, SLNG_TEMPLATE } from "@/models.ts";
import { validateEffectiveStartDateForProgramYear } from "@/utils/dates";

export type LanguageOption = {
  Text: string;
  Value: string;
};

export function buildSlngAddDefaults(record: SLNG): SLNG {
  return {
    ...SLNG_TEMPLATE,
    ReportingLEA: record.ReportingLEA,
    CSPPID: record.CSPPID,
    ProgramYear: record.ProgramYear,
    PreschoolCode: record.PreschoolCode,
    LocalChildId: record.LocalChildId,
    InterviewHomeLanguage1: record.InterviewHomeLanguage1,
    InterviewHomeLanguage2: record.InterviewHomeLanguage2,
    InterviewHomeLanguage3: record.InterviewHomeLanguage3,
    InterviewHomeLanguage4: record.InterviewHomeLanguage4,
    InterviewHomeLanguage5: record.InterviewHomeLanguage5,
    InterviewMostUsedLanguage: record.InterviewMostUsedLanguage,
    Id: "",
    EffectiveStartDate: "",
    EffectiveEndDate: "",
  };
}

type UseSLNGFormLogicArgs = {
  form: UseFormReturn<SLNG, any, SLNG>;
  slng: SLNG;
  resetKey: string;
  languageOptions: LanguageOption[];
  isOpen: boolean;
  isEdit: boolean;
  currentValues: Partial<SLNG>;
};

export function useSLNGFormLogic({
  form,
  slng,
  resetKey,
  languageOptions,
  isOpen,
  isEdit,
  currentValues,
}: UseSLNGFormLogicArgs) {
  const {
    reset,
    setValue,
    control,
    clearErrors,
    register,
    setError,
    getValues,
    trigger,
    watch,
  } = form;
  const lastOpenedKeyRef = useRef<string | null>(null);
  const prevIsOpenRef = useRef<boolean>(isOpen);
  useEffect(() => {
    register("InterviewHomeLanguage1", {
      required: "At least one interview home language is required",
    });
  }, [register]);
  useEffect(() => {
    if (!isOpen) return;
    if (lastOpenedKeyRef.current === resetKey) return;
    lastOpenedKeyRef.current = resetKey;
    if (isEdit) {
      reset(slng);
    } else {
      reset({ ...slng, ...currentValues });
    }
  }, [isOpen, resetKey, reset, slng, isEdit, currentValues]);
  useEffect(() => {
    const wasOpen = prevIsOpenRef.current;
    prevIsOpenRef.current = isOpen;
    if (wasOpen && !isOpen) {
      lastOpenedKeyRef.current = null;
    }
  }, [isOpen]);
  useEffect(() => {
    const subscription = watch((value, { name }) => {
      if (name === "ProgramYear" && value.EffectiveStartDate) {
        trigger("EffectiveStartDate");
      }
    });
    return () => subscription.unsubscribe();
  }, [watch, trigger]);
  const ihl1 = useWatch({ control, name: "InterviewHomeLanguage1" });
  const ihl2 = useWatch({ control, name: "InterviewHomeLanguage2" });
  const ihl3 = useWatch({ control, name: "InterviewHomeLanguage3" });
  const ihl4 = useWatch({ control, name: "InterviewHomeLanguage4" });
  const ihl5 = useWatch({ control, name: "InterviewHomeLanguage5" });
  const selectedValues = [ihl1, ihl2, ihl3, ihl4, ihl5].filter(
    Boolean
  ) as string[];
  const selectedHomeLanguages = selectedValues
    .map((val) => languageOptions.find((o) => o.Value === val))
    .filter(Boolean) as LanguageOption[];
  const onHomeLanguagesChange = (items: LanguageOption[]) => {
    const next = (items ?? []).slice(0, 5);
    setValue("InterviewHomeLanguage1", next[0]?.Value ?? "", {
      shouldDirty: true,
    });
    setValue("InterviewHomeLanguage2", next[1]?.Value ?? "", {
      shouldDirty: true,
    });
    setValue("InterviewHomeLanguage3", next[2]?.Value ?? "", {
      shouldDirty: true,
    });
    setValue("InterviewHomeLanguage4", next[3]?.Value ?? "", {
      shouldDirty: true,
    });
    setValue("InterviewHomeLanguage5", next[4]?.Value ?? "", {
      shouldDirty: true,
    });
    if (next.length > 0) {
      clearErrors("InterviewHomeLanguage1");
    } else {
      setError("InterviewHomeLanguage1", {
        type: "manual",
        message: "At least one interview home language is required",
      });
    }
  };
  const validateEffectiveStartDate = (
    value: string | undefined
  ): string | true => {
    return validateEffectiveStartDateForProgramYear(
      value,
      getValues("ProgramYear")
    );
  };
  return {
    selectedHomeLanguages,
    onHomeLanguagesChange,
    validateEffectiveStartDate,
  };
}
